"use client";

/**
 * Select — responsive dropdown primitive that covers two patterns:
 *  1. Select: pick one active option from a list (desktop Popover / mobile Drawer).
 *  2. Combobox: type-ahead with keyboard navigation, multi-select, optional AI fallback.
 *
 * Item density is controlled per-call via <Select.Item  | "comfortable">.
 * Keyboard navigation engages automatically when <Select.Input> is used and items have `index` props.
 */

import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { VisuallyHidden } from "radix-ui";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "@/lib/utils";
import useIsMobile from "@/hooks/use-is-mobile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Icon, type IconName } from "@/components/ui/icon";
import { ScrollArea } from "@/components/ui/scroll-area";
import { inputVariants, type InputVariantProps } from "@/components/ui/input";

const ITEM_ATTR = "data-select-item";

/* ── Contexts ────────────────────────────────────────────────────────── */

interface ActionsCtx {
  setOpen: (v: boolean) => void;
  toggleOpen: () => void;
  setQuery: (v: string) => void;
  setHighlightedIndex: (i: number | ((prev: number) => number)) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
}

const ActionsContext = createContext<ActionsCtx | null>(null);
const OpenContext = createContext(false);
const QueryContext = createContext("");
const HighlightContext = createContext(-1);
const MobileContext = createContext(false);

function useActions() {
  const ctx = useContext(ActionsContext);
  if (!ctx) throw new Error("Select.* must be used within Select.Root");
  return ctx;
}
const useOpenCtx = () => useContext(OpenContext);
const useQueryCtx = () => useContext(QueryContext);

/** Query string for combobox/search filtering inside `Select.Root`. */
export function useSelectQuery() {
  return useQueryCtx();
}

/** Actions (open state, query) for components rendered inside `Select.Root`. */
export function useSelectActions() {
  return useActions();
}
const useHighlightCtx = () => useContext(HighlightContext);
const useMobileCtx = () => useContext(MobileContext);

/* ── Root ────────────────────────────────────────────────────────────── */

interface RootProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, the popover traps focus (Radix modal behavior). */
  modal?: boolean;
  /** When false, always render as a Popover (never switch to a mobile Drawer). Default true. */
  responsive?: boolean;
  highlightedIndex?: number;
  onHighlightedIndexChange?: (i: number | ((prev: number) => number)) => void;
  children: ReactNode;
}

function SelectRoot({
  open: controlledOpen,
  onOpenChange,
  modal,
  responsive = true,
  highlightedIndex: controlledHighlight,
  onHighlightedIndexChange,
  children,
}: RootProps) {
  const isMobileDevice = useIsMobile();
  const isMobile = responsive && isMobileDevice;

  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const openRef = useRef(open);
  const [query, setQuery] = useState("");
  const [internalHighlight, setInternalHighlight] = useState(-1);
  const highlightedIndex = controlledHighlight ?? internalHighlight;
  const setHighlightedIndex = useCallback(
    (v: number | ((prev: number) => number)) => {
      if (onHighlightedIndexChange) onHighlightedIndexChange(v);
      if (controlledHighlight === undefined) setInternalHighlight(v);
    },
    [controlledHighlight, onHighlightedIndexChange],
  );

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const setOpen = useCallback(
    (v: boolean) => {
      if (!v) {
        setHighlightedIndex(-1);
        setQuery("");
      }
      onOpenChange?.(v);
      if (controlledOpen === undefined) setInternalOpen(v);
    },
    [controlledOpen, onOpenChange, setHighlightedIndex],
  );
  const toggleOpen = useCallback(() => {
    setOpen(!openRef.current);
  }, [setOpen]);

  const anchorRef = useRef<HTMLElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const actions = useMemo<ActionsCtx>(
    () => ({
      setOpen,
      toggleOpen,
      setQuery,
      setHighlightedIndex,
      anchorRef,
      dropdownRef,
    }),
    [setOpen, toggleOpen, setHighlightedIndex],
  );

  const tree = (
    <ActionsContext.Provider value={actions}>
      <OpenContext.Provider value={open}>
        <QueryContext.Provider value={query}>
          <HighlightContext.Provider value={highlightedIndex}>
            <MobileContext.Provider value={isMobile}>
              {children}
            </MobileContext.Provider>
          </HighlightContext.Provider>
        </QueryContext.Provider>
      </OpenContext.Provider>
    </ActionsContext.Provider>
  );

  if (isMobile) {
    return (
      <DrawerPrimitive.Root open={open} onOpenChange={setOpen} modal={modal}>
        {tree}
      </DrawerPrimitive.Root>
    );
  }

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={setOpen}
      modal={modal ?? false}
    >
      {tree}
    </PopoverPrimitive.Root>
  );
}

/* ── Trigger ─────────────────────────────────────────────────────────── */

type TriggerProps = Omit<ComponentPropsWithoutRef<"button">, "size"> &
  InputVariantProps & {
    children: ReactNode;
    /**
     * Whether the trigger is showing a selected value. When provided, sets a
     * `data-active` attribute so the trigger's text color flips between
     * `text-foreground` and `text-muted-foreground` and child slots (icon,
     * label) inherit the right color via `currentColor`.
     */
    active?: boolean;
  };

const triggerActiveClasses =
  "data-[active=true]:text-foreground data-[active=false]:text-muted-foreground";

const SelectTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
  function SelectTrigger(
    { children, size, variant, className, active, ...props },
    ref,
  ) {
    const isMobile = useMobileCtx();
    const { anchorRef } = useActions();

    const mergedRef = useCallback(
      (node: HTMLButtonElement | null) => {
        anchorRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [anchorRef, ref],
    );

    const dataActive =
      active === undefined ? undefined : active ? "true" : "false";

    if (isMobile) {
      return (
        <DrawerPrimitive.Trigger
          ref={mergedRef}
          type="button"
          className={cn(
            inputVariants({ size, variant }),
            "cursor-pointer",
            triggerActiveClasses,
            className,
          )}
          data-select-anchor
          data-active={dataActive}
          {...props}
        >
          {children}
        </DrawerPrimitive.Trigger>
      );
    }

    return (
      <PopoverPrimitive.Trigger
        ref={mergedRef}
        type="button"
        className={cn(
          inputVariants({ size, variant }),
          "cursor-pointer",
          triggerActiveClasses,
          className,
        )}
        data-select-anchor
        data-active={dataActive}
        {...props}
      >
        {children}
      </PopoverPrimitive.Trigger>
    );
  },
);

/* ── TriggerContent (label slot inside Trigger) ──────────────────────── */

function SelectTriggerContent({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"span">) {
  return (
    <span
      data-slot="select-trigger-content"
      className={cn("flex-1 truncate text-left text-sm", className)}
      {...props}
    >
      {children}
    </span>
  );
}

/* ── Anchor (bare wrapper, desktop only) ─────────────────────────────── */

type AnchorProps = ComponentPropsWithoutRef<"div">;

function SelectAnchor({ className, children, ...props }: AnchorProps) {
  const isMobile = useMobileCtx();
  const { anchorRef } = useActions();
  const setAnchor = useCallback(
    (node: HTMLDivElement | null) => {
      anchorRef.current = node;
    },
    [anchorRef],
  );

  if (isMobile) {
    return (
      <div ref={setAnchor} className={className} {...props}>
        {children}
      </div>
    );
  }

  return (
    <PopoverPrimitive.Anchor
      ref={setAnchor}
      data-select-anchor
      className={className}
      {...props}
    >
      {children}
    </PopoverPrimitive.Anchor>
  );
}

/* ── Icon (chevron grabber) ──────────────────────────────────────────── */

function SelectIcon({
  className,
  ...props
}: Omit<React.ComponentProps<typeof Icon>, "name">) {
  return (
    <Icon
      name="IconChevronGrabberVertical"
      size={16}
      aria-hidden="true"
      className={cn("text-heading opacity-40 shrink-0 ml-2", className)}
      {...props}
    />
  );
}

/* ── Input (bare input with keyboard nav) ────────────────────────────── */

interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

const SelectInput = forwardRef<HTMLInputElement, InputProps>(
  function SelectInput(
    { className, onKeyDown, onChange, value, ...props },
    ref,
  ) {
    const open = useOpenCtx();
    const query = useQueryCtx();
    const highlightedIndex = useHighlightCtx();
    const { setOpen, setQuery, setHighlightedIndex, dropdownRef } =
      useActions();
    const internalRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => internalRef.current!);

    useEffect(() => {
      if (value !== undefined) setQuery(String(value));
    }, [value, setQuery]);

    const getItemCount = useCallback(
      () => dropdownRef.current?.querySelectorAll(`[${ITEM_ATTR}]`).length ?? 0,
      [dropdownRef],
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          if (!open) {
            setOpen(true);
            return;
          }
          const total = getItemCount();
          if (total > 0)
            setHighlightedIndex((prev) => (prev < total - 1 ? prev + 1 : 0));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          const total = getItemCount();
          if (total > 0)
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : total - 1));
        } else if (e.key === "Enter") {
          e.preventDefault();
          const container = dropdownRef.current;
          if (container && highlightedIndex >= 0) {
            const items = container.querySelectorAll(`[${ITEM_ATTR}]`);
            (items[highlightedIndex] as HTMLElement | undefined)?.click();
          }
        } else if (e.key === "Escape") {
          setOpen(false);
        }
        onKeyDown?.(e);
      },
      [
        dropdownRef,
        getItemCount,
        highlightedIndex,
        onKeyDown,
        open,
        setHighlightedIndex,
        setOpen,
      ],
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
        onChange?.(e);
      },
      [onChange, setQuery],
    );

    const displayValue = value !== undefined ? value : query;

    return (
      <input
        ref={internalRef}
        type="text"
        data-slot="select-input"
        autoComplete="off"
        value={displayValue}
        className={cn(
          "flex-1 text-sm bg-transparent text-heading placeholder:text-contrast/30 outline-none min-w-0",
          className,
        )}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  },
);

/* ── Content (Popover desktop / Drawer mobile) ───────────────────────── */

interface ContentProps extends ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> {
  /** When true, the dropdown width matches the trigger width via `--radix-popover-trigger-width`. Default true. */
  matchTriggerWidth?: boolean;
}

function SelectContent({
  className,
  align = "start",
  side = "bottom",
  sideOffset = 4,
  matchTriggerWidth = true,
  children,
  ...props
}: ContentProps) {
  const isMobile = useMobileCtx();
  const open = useOpenCtx();
  const highlightedIndex = useHighlightCtx();
  const { anchorRef, dropdownRef } = useActions();

  useEffect(() => {
    if (highlightedIndex < 0 || !dropdownRef.current) return;
    const items = dropdownRef.current.querySelectorAll(`[${ITEM_ATTR}]`);
    (items[highlightedIndex] as HTMLElement | undefined)?.scrollIntoView({
      block: "nearest",
    });
  }, [dropdownRef, highlightedIndex, open]);

  if (isMobile) {
    const drawerClassName = className
      ?.split(" ")
      .filter(
        (c) =>
          !c.startsWith("w-") &&
          !c.startsWith("max-w-") &&
          !c.startsWith("min-w-"),
      )
      .join(" ");

    return (
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <DrawerPrimitive.Content
          data-portal-dropdown="true"
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-xl border border-border border-b-0 bg-surface-elevated focus:outline-none max-h-[85dvh]",
            drawerClassName,
          )}
        >
          <VisuallyHidden.Root>
            <DrawerPrimitive.Title>Select</DrawerPrimitive.Title>
          </VisuallyHidden.Root>
          <div className="mx-auto mt-3 mb-1 h-1 w-8 rounded-full bg-contrast/20" />
          {children}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    );
  }

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={dropdownRef}
        align={align}
        side={side}
        sideOffset={sideOffset}
        role="listbox"
        className={cn(
          "z-[100] flex min-h-0 flex-col overflow-hidden bg-surface-elevated rounded-lg p-0 text-heading shadow-[var(--modal-shadow)] outline-none",
          matchTriggerWidth &&
            "w-[var(--radix-popover-trigger-width)] max-w-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        {...props}
        // Portaled outside Dialog.Popup — without this marker, base-ui treats
        // option presses as Modal outside-press and dismisses the parent dialog.
        data-portal-dropdown="true"
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

/* ── Separator ───────────────────────────────────────────────────────── */

function SelectSeparator({ className }: { className?: string }) {
  return <div className={cn("h-px bg-contrast/10", className)} />;
}

/* ── Search (pre-styled row: icon + Input + optional spinner) ────────── */

interface SearchProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  containerClassName?: string;
  showSeparator?: boolean;
  iconName?: IconName;
  loading?: boolean;
}

const SelectSearch = forwardRef<HTMLInputElement, SearchProps>(
  function SelectSearch(
    {
      containerClassName,
      className,
      showSeparator = true,
      iconName = "IconMagnifyingGlass",
      loading,
      ...props
    },
    ref,
  ) {
    return (
      <>
        <div
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-2",
            containerClassName,
          )}
        >
          <div className="flex items-center justify-center size-5 shrink-0 opacity-50">
            <Icon name={iconName} size={16} className="text-heading" />
          </div>
          <SelectInput
            ref={ref}
            className={cn("flex-1", className)}
            {...props}
          />
          {loading ? (
            <Icon
              name="IconLoadingCircle"
              size={14}
              className="text-heading opacity-40 animate-spin shrink-0"
            />
          ) : null}
        </div>
        {showSeparator && <SelectSeparator />}
      </>
    );
  },
);

/* ── List (scroll container) ─────────────────────────────────────────── */

interface ListProps extends ComponentPropsWithoutRef<"div"> {
  viewportClassName?: string;
  /** Ref to the scrollable viewport — e.g. to root an infinite-scroll observer. */
  viewportRef?: React.Ref<HTMLDivElement>;
  /**
   * Constrain ScrollArea content to the viewport width so long item labels
   * truncate instead of forcing horizontal overflow (`min-width: fit-content`).
   */
  constrainContentWidth?: boolean;
}

function SelectList({
  className,
  viewportClassName,
  viewportRef,
  constrainContentWidth,
  children,
  ...props
}: ListProps) {
  return (
    <ScrollArea
      className={cn("min-h-0 max-h-72", className)}
      viewportClassName={cn("overscroll-contain py-1", viewportClassName)}
      viewportRef={viewportRef}
      constrainContentWidth={constrainContentWidth}
      {...props}
    >
      {children}
    </ScrollArea>
  );
}

interface ListPendingProps {
  count?: number;
  className?: string;
}

function SelectListPending({ count = 4, className }: ListPendingProps) {
  return (
    <div className={cn("flex flex-col gap-0.5 px-1", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-1.5 h-8 px-1.5">
          <div className="size-4 rounded-[3px] bg-contrast/6 shimmer shimmer-bg text-foreground shrink-0" />
          <div
            className="h-3 rounded-[3px] bg-contrast/6 shimmer shimmer-bg text-foreground"
            style={{ width: `${55 + i * 12}%` }}
          />
        </div>
      ))}
    </div>
  );
}

function SelectListEmpty({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "px-2.5 py-2 text-sm text-contrast/40 text-center",
        className,
      )}
      {...props}
    >
      {children ?? "No results"}
    </div>
  );
}

function SelectListMustType({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "px-2.5 py-2 text-[13px] text-contrast/40 text-center",
        className,
      )}
    >
      {children ?? "Start typing to search..."}
    </div>
  );
}

function SelectListError({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "px-2.5 py-2 text-[13px] text-destructive text-center",
        className,
      )}
    >
      {children ?? "Something went wrong"}
    </div>
  );
}

/* ── Centered empty state (icon + title + description) ───────────────── */

function SelectEmpty({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-0.5 px-3 py-10",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function SelectEmptyTitle({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"p">) {
  return (
    <p
      className={cn("mt-1 text-sm font-medium text-heading", className)}
      {...props}
    >
      {children}
    </p>
  );
}

function SelectEmptyDescription({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"p">) {
  return (
    <p
      className={cn(
        "mt-0.5 text-center text-xs leading-normal text-contrast/60",
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
}

/* ── CategoryHeader ──────────────────────────────────────────────────── */

function SelectCategoryHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("px-2.5 pt-2 pb-1", className)}>
      <span className="text-[10px] font-semibold tracking-[0.5px] text-ai">
        {children}
      </span>
    </div>
  );
}

/* ── AskArbi ─────────────────────────────────────────────────────────── */

interface AskArbiProps {
  index?: number;
  onSelect?: (query: string) => void;
  className?: string;
}

function SelectAskArbi({ index = 0, onSelect, className }: AskArbiProps) {
  const query = useQueryCtx();
  const highlightedIndex = useHighlightCtx();
  const { setHighlightedIndex } = useActions();
  const highlighted = highlightedIndex === index;
  const trimmedQuery = query.trim();

  return (
    <div className={cn("px-1", className)}>
      <button
        type="button"
        data-select-item
        className={cn(
          "w-full flex items-center gap-1.5 h-8 px-1.5 rounded cursor-pointer transition-colors",
          highlighted ? "bg-contrast/6" : "hover:bg-contrast/3",
        )}
        onMouseDown={(e) => {
          e.preventDefault();
          onSelect?.(trimmedQuery);
        }}
        onMouseEnter={() => setHighlightedIndex(index)}
        onClick={(e) => {
          if (e.detail === 0) onSelect?.(trimmedQuery);
        }}
      >
        <div className="flex items-center gap-2">
          <Icon name="IconSparklesTwo2" className="text-ai size-3.5" />
          <span
            className="text-sm font-normal leading-5 bg-clip-text text-transparent whitespace-nowrap"
            style={{
              backgroundImage:
                "linear-gradient(90deg, #ffdb8e 32%, #ffdfb2 42%, #ffaf53 64%)",
            }}
          >
            Ask Arbi
          </span>
        </div>
        <span className="text-sm font-normal text-contrast/40 truncate ml-auto">
          &ldquo;{trimmedQuery}&rdquo;
        </span>
      </button>
    </div>
  );
}

/* ── Item ────────────────────────────────────────────────────────────── */

interface ItemProps extends Omit<
  ComponentPropsWithoutRef<"button">,
  "onSelect"
> {
  selected?: boolean;
  index?: number;
  onSelect?: () => void;
}

const SelectItem = forwardRef<HTMLButtonElement, ItemProps>(function SelectItem(
  {
    selected = false,
    index,
    onSelect,
    className,
    children,
    onClick,
    onMouseDown,
    onMouseEnter,
    ...props
  },
  ref,
) {
  const highlightedIndex = useHighlightCtx();
  const { dropdownRef, setHighlightedIndex } = useActions();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  useImperativeHandle(ref, () => buttonRef.current!);

  const resolveIndex = useCallback((): number => {
    if (index !== undefined) return index;
    const container = dropdownRef.current;
    if (!container || !buttonRef.current) return -1;
    const items = container.querySelectorAll(`[${ITEM_ATTR}]`);
    return Array.from(items).indexOf(buttonRef.current);
  }, [dropdownRef, index]);

  const highlighted = index !== undefined ? highlightedIndex === index : false;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Preserve focus on any adjacent Select.Input (combobox pattern) by
      // preventing the button from stealing focus on mousedown.
      e.preventDefault();
      onSelect?.();
      onMouseDown?.(e);
    },
    [onMouseDown, onSelect],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Only fire onSelect from click when it's keyboard-driven (detail === 0);
      // pointer-driven clicks already ran onSelect via mousedown.
      if (e.detail === 0) onSelect?.();
      onClick?.(e);
    },
    [onClick, onSelect],
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const idx = resolveIndex();
      if (idx >= 0) setHighlightedIndex(idx);
      onMouseEnter?.(e);
    },
    [onMouseEnter, resolveIndex, setHighlightedIndex],
  );

  return (
    <div className="px-1">
      <button
        ref={buttonRef}
        type="button"
        data-select-item
        className={cn(
          "w-full flex items-center gap-1.5 h-8 px-1.5 rounded-md cursor-pointer transition-colors",
          selected
            ? "bg-ai/8"
            : highlighted
              ? "bg-contrast/6"
              : "hover:bg-contrast/3",
          "disabled:opacity-60 disabled:cursor-default",
          className,
        )}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        {...props}
      >
        {children}
      </button>
    </div>
  );
});

/* ── Item parts ──────────────────────────────────────────────────────── */

function SelectItemIcon({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("shrink-0", className)}>{children}</div>;
}

function SelectItemAvatar({
  src,
  className,
}: {
  src?: string | null;
  className?: string;
}) {
  return (
    <SelectItemIcon className={className}>
      <Avatar className="size-4 shrink-0 rounded-xs">
        <AvatarImage src={src} alt="" />
        <AvatarFallback />
      </Avatar>
    </SelectItemIcon>
  );
}

function SelectItemLabel({
  children,
  selected,
  className,
}: {
  children: ReactNode;
  selected?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "flex-1 text-sm font-normal leading-5 truncate text-left",
        selected ? "text-ai" : "text-contrast/75",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SelectItemDescription({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("truncate text-[11px] text-contrast/40", className)}>
      {children}
    </div>
  );
}

function SelectItemCount({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "text-[10px] font-medium text-ai shrink-0 tabular-nums ml-auto",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SelectItemCheck({ className }: { className?: string }) {
  return (
    <Icon
      name="IconCheckmark1"
      size={12}
      className={cn("text-ai shrink-0 ml-auto", className)}
    />
  );
}

/* ── ShowMore / Footer ───────────────────────────────────────────────── */

interface ShowMoreProps extends ComponentPropsWithoutRef<"button"> {
  remaining: number;
}

function SelectShowMore({
  remaining,
  className,
  children,
  ...props
}: ShowMoreProps) {
  return (
    <button
      type="button"
      className={cn(
        "w-full px-3 py-2 text-center text-xs text-contrast/40 hover:text-contrast/60 hover:bg-contrast/4 transition-colors cursor-pointer",
        className,
      )}
      {...props}
    >
      {children ?? `Show more (${remaining} remaining)`}
    </button>
  );
}

function SelectFooter({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={cn(
        "flex border-t border-contrast/6 items-center justify-end px-2.5 py-2",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/* ── Assembled export ────────────────────────────────────────────────── */

export const Select = Object.assign(SelectRoot, {
  Root: SelectRoot,
  Trigger: SelectTrigger,
  TriggerContent: SelectTriggerContent,
  Anchor: SelectAnchor,
  Icon: SelectIcon,
  Content: SelectContent,
  Input: SelectInput,
  Search: SelectSearch,
  Separator: SelectSeparator,
  List: SelectList,
  ListPending: SelectListPending,
  ListEmpty: SelectListEmpty,
  ListMustType: SelectListMustType,
  ListError: SelectListError,
  Empty: SelectEmpty,
  EmptyTitle: SelectEmptyTitle,
  EmptyDescription: SelectEmptyDescription,
  CategoryHeader: SelectCategoryHeader,
  AskArbi: SelectAskArbi,
  Item: SelectItem,
  ItemIcon: SelectItemIcon,
  ItemAvatar: SelectItemAvatar,
  ItemLabel: SelectItemLabel,
  ItemDescription: SelectItemDescription,
  ItemCount: SelectItemCount,
  ItemCheck: SelectItemCheck,
  ShowMore: SelectShowMore,
  Footer: SelectFooter,
});
